/**
 * Required packages/kit management for multi-product bundle sales.
 * Handles parent-child product relationships, package flow navigation,
 * cart operations, and thank you page display.
 * 
 * @class
 * @description This class provides:
 * - Parent product "add to cart" handling with unique IDs
 * - Child product collection flow through kit
 * - Cart item linking via properties
 * - Recommended products handling
 * - Session-based state management
 * 
 * @requires jQuery (for trigger events)
 * @requires getCookie, setCookie, eraseCookie, getQueryParam (global functions)
 * @requires MultiStorefront (global API client)
 * 
 * @example
 * // Automatically instantiates on page load
 * new RequiredPackages();
 */
class RequiredPackages {

  /**
   * Creates a new RequiredPackages instance.
   * Retrieves customer and store IDs from window.
   * @constructor
   */
  constructor() {
    /** @type {number|null} Current customer ID */
    this.customerId = typeof window.customerId != "undefined" ? parseInt(window.customerId) : null;
    /** @type {number|null} Current store ID */
    this.storeId = typeof window.storeId != "undefined" ? parseInt(window.storeId) : null;
    /** @type {number|null} Current kit/package ID */
    this.kitId = null;

    this.bindEvents();
  }

  /**
   * Binds event listeners for package/kit interactions.
   * Handles all buttons and form submissions related to required packages.
   * 
   * @returns {void}
   * @sideEffect Adds event listeners to DOM elements
   */
  bindEvents() {
    let _this = this;

    // Remove package parent button - clears all session data
    if (document.querySelector('.remove-package-parent')) {
      document.querySelector('.remove-package-parent').addEventListener('click', (e) => {
        sessionStorage.removeItem('required_package_addon');
        eraseCookie('required_package_initialized');
        sessionStorage.removeItem('required_package_handle');
        sessionStorage.removeItem('required_package_unique_id');
        sessionStorage.removeItem('required_package_parent_id');
        sessionStorage.removeItem('required_package_products');
        sessionStorage.removeItem('required_package_recommended');
      });
    }

    // Add kit to cart button on parent product
    if (document.querySelector('.add-kit-to-cart')) {
      document.querySelector('.add-kit-to-cart').addEventListener('click', (e) => {
        e.preventDefault();

        let el = e.target;

        // Validate and add parent to cart
        if (el.dataset.addToCart === 'true') {
          let valid = _this.validateProductForm(el) && _this.validateRequiredLogo();
          if (!valid) {
            e.stopPropagation();
            return false;
          } else {
            _this.addParentProductToCart(el).then(
              (response) => {
                // Redirect to next step
                window.location.href = el.getAttribute('href');
              },
              (error) => {
                console.log('error', error)
              }
            ).catch(error => {
              console.log('error', error)
            });
          }
        } else {
          // Just navigate to next page
          window.location.href = el.getAttribute('href');
        }
      });
    }

    // Continue button on child product (package item)
    if (document.querySelector('.add-required-package-product-to-cart')) {
      document.querySelectorAll('.add-required-package-product-to-cart').forEach((el) => {
        el.addEventListener('click', (e) => {
          e.preventDefault();
          let valid = _this.validateProductForm(e.target) && _this.validateRequiredLogo();
          if (!valid) {
            e.stopPropagation();
            return false;
          } else {
            // Trigger variant selection if needed
            if (document.querySelector('.option-selector:not(.hidden):not(.swatch-selector) .js-option:checked')) {
              $('.option-selector:not(.hidden):not(.swatch-selector) .js-option:checked').trigger('click');
              document.dispatchEvent(new Event('theme:variantchanged'));
            }
            
            // Delay to ensure variant is selected
            setTimeout(function () {
              fetch('/cart.js').then(res => res.json()).then(cart => {
                _this.addChildProductToCart(e.target, cart).then(
                  (response) => {
                    window.location.href = e.target.getAttribute('href');
                  },
                  (error) => {
                    console.log('error', error)
                  }
                ).catch(error => {
                  console.log('error', error)
                });
              });
            }, 50)
          }
        });
      })
    }

    // Addon product to cart
    if (document.querySelector('.required-package-addon-add')) {
      document.querySelector('.required-package-addon-add').addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        // Get variant and quantity from form
        let id = e.target.closest('form').querySelector('[name="id"]').value;
        let qty = e.target.closest('.product-form').querySelector('input[name="quantity"]').value;

        // Build properties with package metadata
        let properties = {};
        properties['_required_package_addon'] = true;
        properties['_kit_id'] = _this.kitId;

        let formData = {
          'id': id,
          'quantity': qty,
          'properties': properties
        };

        // Add to cart
        fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        }).then(response => response.json())
          .then((response) => {
            window.location.href = e.target.getAttribute('href');
          }).catch((e) => {
          console.log('error', e)
        });
      });
    }

    // Add recommended products to cart
    if (document.querySelector('.required-packages-add-recommended-to-cart')) {
      document.querySelector('.required-packages-add-recommended-to-cart').addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        _this.submitRequiredPackageRecommended($(this));
        return false;
      });
    }

    // Back button in package flow
    if (document.querySelector('.required-package-back')) {
      document.querySelector('.required-package-back').addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        window.location.href = e.target.getAttribute('href');
        return false;
      });
    }

    // DOM ready - initialize package flow
    document.addEventListener('DOMContentLoaded', (e) => {
      _this.kitId = getQueryParam('kit', window.location.href);
      
      // Thank you page handling
      if (document.querySelector('.required-packages-thank-you') != null) {
        _this.initRequiredPackageThankYou();
      }
      // Invalid kit - clean up and disable
      else if (!_this.kitId || !getCookie('required_package_initialized')) {
        window.history.replaceState(null, '', window.location.href.replace('?kit='+_this.kitId, ''));
        let uniqueId = sessionStorage.getItem('required_package_unique_id');
        if(uniqueId) {
          _this.clearKitItems(uniqueId);
        }
        _this.clearSession();
        document.querySelector('body').classList.remove('required-package-initialized');
      }
      // Valid kit - initialize flow
      else {
        let uniqueId = sessionStorage.getItem('required_package_unique_id');
        if(!uniqueId) {
          window.history.replaceState(null, '', window.location.href.replace('?kit='+_this.kitId, ''));
          document.querySelector('body').classList.remove('required-package-initialized');
        }
        else {
          let isParent = false;
          if (document.querySelector('body').classList.contains('is-kit-product')) {
            isParent = true;
          }
          else {
            document.querySelector('body').classList.add('required-package-initialized');
          }

          _this.initRequiredPackage(isParent);
        }
      }
    });

    // Prevent disabled quantity buttons from clicking
    document.querySelectorAll('.quantity-selector__button.is-disabled').forEach(el => {
      el.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
      });
    });

    // Handle browser back/forward navigation
    window.addEventListener('pageshow', function(event) {
      if (event.persisted) {
        if(document.querySelector('body').classList.contains('required-package-initialized') && !getCookie('required_package_initialized')) {
          window.location.reload();
        }
      }
    });
  }

  /**
   * Adds parent kit product to cart with unique identifier.
   * Generates unique ID to track the kit and stores related data in session/cookies.
   * 
   * @param {HTMLElement} btn - The button element clicked
   * @returns {Promise<Object>} Resolves on success with status/message
   */
  addParentProductToCart(btn) {
    if (!btn) return;

    return new Promise(async (resolve, reject) => {
      let variantId = document.querySelector('input[name="id"]');
      if (!variantId) return;
      variantId = variantId.value;

      if (!variantId) {
        reject({
          status: null,
          message: 'Wrong/missing product ID',
          variantId: null,
        });
      }

      // Generate unique ID for this kit and keep embroidery/logo properties from the form
      let properties = {};
      let uniqueId = Math.random().toString(36).slice(2, 9);
      let form = document.querySelector('.product-purchase-form');
      if (form) {
        new FormData(form).forEach(function (value, key) {
          if (key.includes('properties')) {
            key = key.replace('properties[', '').replace(']', '');
            if (key) properties[key] = value;
          }
        });
      }
      properties['_required_package_unique_id'] = uniqueId.toString();
      properties['_required_package_parent'] = true;

      let formData = {
        items: [{
          'id': variantId,
          'quantity': 1,
          'properties': properties
        }]
      };

      fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      }).then(response => response.json())
        .then((response) => {
          if (typeof response.items != 'undefined' && response.items.length > 0) {
            // Store session data
            sessionStorage.setItem('required_package_unique_id', uniqueId);
            if (typeof btn.dataset.kitid != "undefined") {
              setCookie('required_package_initialized', btn.dataset.kitid);
            }
            if (typeof btn.dataset.addonHandle != "undefined") {
              sessionStorage.setItem('required_package_addon', btn.dataset.addonHandle);
            }
            if (typeof btn.dataset.handle != "undefined") {
              sessionStorage.setItem('required_package_handle', btn.dataset.handle);
            }
            if (typeof btn.dataset.recommendedProducts != "undefined") {
              sessionStorage.setItem('required_package_recommended', btn.dataset.recommendedProducts);
            }
            if (typeof btn.dataset.kitProducts != "undefined") {
              sessionStorage.setItem('required_package_products', btn.dataset.kitProducts);
            }

            // Store parent item ID
            response.items.forEach((item) => {
              if (item.id == variantId) {
                sessionStorage.setItem('required_package_parent_id', item.id);
              }
            })

            resolve({
              status: 200,
              message: 'Success',
              variantId: variantId
            });
            return;
          }

          reject({
            response: response,
            message: response.message,
            variantId: variantId,
          });
        }).catch((e) => {
        reject({
          response: e,
          message: e.message,
          variantId: variantId,
        });
      })
    });
  }

  /**
   * Adds a child product to the existing kit in cart.
   * Links child item to parent via unique ID and updates parent properties.
   * 
   * @param {HTMLElement} btn - Button element clicked
   * @param {Object} cart - Current cart data
   * @returns {Promise<Object>} Resolves on success
   */
  addChildProductToCart(btn, cart) {
    return new Promise(async (resolve, reject) => {
    let _this = this;
    if (!btn || !cart) {
      reject({
        message: 'Missing required parameters (btn/cart)'
      });
      return;
    }
    
    // Get stored IDs
    let parentId = sessionStorage.getItem('required_package_parent_id');
    let uniqueId = sessionStorage.getItem('required_package_unique_id');
    if (!parentId || !uniqueId) {
      reject({
        message: 'Missing required parameters (parentId/uniqueId)'
      });
      return;
    }

    let childInCart = false;
    let productId = btn.closest('.required-packages-container')?.dataset.productid;

    if(!productId || typeof productId == 'undefined') {
      reject({
        message: 'Missing required parameters (productId)'
      });
      return;
    }

    // Check if product already in cart as child
    cart.items.some((item) => {
      if (item.id == parseInt(parentId)) {
        let parentProperties = item.properties;

        if(parentProperties.hasOwnProperty('_required_package_child_items') &&
        parentProperties._required_package_child_items != '') {
          let itemsJson = JSON.parse(parentProperties._required_package_child_items);
          for(const [key, value] of Object.entries(itemsJson)) {
            if(value.properties.productId == productId) {
              childInCart = key;
            }
          }
        }
        return true;
      }
    });

    // Get selected variant
    let variantId = document.querySelector('input[name="id"]');
    variantId = variantId.value;

    if (!variantId) {
      reject({
        status: null,
        message: 'Wrong/missing product ID',
        variantId: null,
      });
    }

    // Find parent item to update
    let parentKey = parentId;
    let properties = {};
    cart.items.forEach((item) => {
      if (item.id == parentId &&
        item.properties?.hasOwnProperty('_required_package_unique_id') &&
        item.properties?._required_package_unique_id == uniqueId
      ) {
        properties = item.properties;
        parentKey = item.key;
      }
    });

    let currentStep = btn.dataset.currentStep;

    // Get form data
    let attributes = cart.attributes;
    let newAttributes = {};
    let newProperties = {};
    let form = document.querySelector('.product-purchase-form');
    if (!form) {
      reject({
        message: 'Missing required parameters (form)'
      });
      return;
    }
    var formData = new FormData(form);

    var currentForm = {};
    formData.forEach(function (value, key) {
      currentForm[key] = value;
    });

    // Separate attributes and properties
    for (let [key, value] of Object.entries(currentForm)) {
      if (key.includes('attributes')) {
        key = key.replace('attributes[', '');
        key = key.replace(']', '');
        newAttributes[key] = value;
      } else if (key.includes('properties')) {
        key = key.replace('properties[', '');
        key = key.replace(']', '');
        newProperties[key] = value;
      }
    }

    newProperties.uniqueId = uniqueId;
    newProperties.productId = productId;

    // Update child items object
    if (!properties.hasOwnProperty('_required_package_child_items') || properties['_required_package_child_items'] == '') {
      properties['_required_package_child_items'] = {};
    } else {
      properties['_required_package_child_items'] = JSON.parse(properties['_required_package_child_items']);
    }

    properties['_required_package_child_items'][variantId] = {
      quantity: currentForm.quantity,
      properties: newProperties
    };

    // Remove old entry if exists
    if(properties['_required_package_child_items'].hasOwnProperty(childInCart)) {
      delete properties['_required_package_child_items'][childInCart];
    }

    properties['_required_package_child_items'] = JSON.stringify(properties['_required_package_child_items']);

    // Build update payload - always quantity 1 for kit
    let updates = {
      'id': parentKey,
      'quantity': 1,
      'properties': properties
    };

    // Update cart attributes if changed
    if (attributes != newAttributes) {
      attributes = {...attributes, ...newAttributes};
      await _this.updateCartAttributes(attributes);
    }

    // Update cart item
    fetch('/cart/change.js', {
      'method': 'POST',
      'headers': {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(updates)
    }).then(response => {

      if (typeof response.status != 'undefined' && response.status == 200) {
        resolve({
          status: 200,
          message: 'Success',
          variantId: variantId
        });
        return;
      }

      reject({
        response: response,
        message: response.message,
        variantId: variantId,
      });
    });
    });
  }

  /**
   * Updates cart attributes via API.
   * 
   * @param {Object} attributes - Attributes to update
   * @returns {Promise<Object>} Resolves on success
   */
  updateCartAttributes(attributes) {
    let updates = {
      attributes: attributes
    }

    return new Promise(async (resolve, reject) => {
      fetch('/cart/update.js', {
        'method': 'POST',
        'headers': {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      }).then(response => {

        if (typeof response.status != 'undefined' && response.status == 200) {
          resolve({
            status: 200,
            message: 'Success',
          });
          return;
        }

        reject({
          response: response,
          message: response.message,
        });
      });
    });
  }

  /**
   * Validates product form custom fields.
   * Checks required fields and marks personalized items.
   * 
   * @param {HTMLElement} btn - Form button element
   * @returns {boolean} True if form is valid
   */
  validateProductForm(btn) {
    let form = btn.closest('form');

    let valid = true;
    let personalized = false;

    // Check custom fields
    if (form.querySelector('.field-container:not(.gift-card-field) .custom-field')) {
      form.querySelectorAll('.field-container:not(.gift-card-field) .custom-field').forEach((field) => {
        if (field.val().length > 1) personalized = true;

        // Validate required
        if (field.hasClass('required') && field.val().length < 1) {
          valid = false;
          field.closest('.field-container').addClass('error');
        } else if (field.hasClass('required') && field.hasClass('select') && field.val() == '0') {
          valid = false;
          field.closest('.field-container').addClass('error');
        } else {
          field.removeClass('error');
          field.closest('.field-container').removeClass('error');
        }
      });

      // Mark as personalized if has content
      if (personalized) {
        if (form.querySelector('input[name="properties[_personalized]"]').length < 1) {
          form.append('<input type="hidden" name="properties[_personalized]" value="true"/>');
        } else form.querySelector('input[name="properties[_personalized]"]').val('true');
      }
    }

    return valid;
  };

  /**
   * Ensures a required logo is selected before kit continue / add-to-cart.
   * Re-applies the pre-select first so a missed page-load race cannot skip the kit.
   *
   * @returns {boolean} True if no logo is required or one is selected
   */
  validateRequiredLogo() {
    if (typeof pdpEmbroidery === 'undefined' || !pdpEmbroidery) return true;
    if (typeof pdpEmbroidery.toggleLogoCheckedIfRequired === 'function') {
      pdpEmbroidery.toggleLogoCheckedIfRequired();
    }
    if (typeof pdpEmbroidery.validateEmbroidery === 'function') {
      return pdpEmbroidery.validateEmbroidery();
    }
    return true;
  }

  /**
   * Submits recommended product to cart from thank you page.
   * Finds matching variant and adds to cart.
   * 
   * @param {jQuery} button - jQuery button object
   * @returns {void}
   */
  submitRequiredPackageRecommended(button) {
    let form = button.closest('form')[0];
    let formData = new FormData(form);

    let variants = form.closest('.product-card').dataset.variants;
    if (variants != null) variants = JSON.parse(variants);

    // Get selected options and quantity
    let values = [];
    let qty = 1
    for (let pair of formData.entries()) {
      if (pair[0] === 'Size' || pair[0] === 'Color') {
        values.push(pair[1]);
      }
      if (pair[0] === 'quantity') qty = pair[1];
    }

    // Find matching variant
    let variantId = null;
    if (variants != null) {
      variants.forEach(function (variant) {
        let useThisVariant = true;
        values.forEach(function (value) {
          if (!variant.options.includes(value)) {
            useThisVariant = false;
          }
        });

        if (useThisVariant) {
          variantId = variant.id;
        }
      });
    }

    // Add to cart if variant found
    if (variantId != null) {
      form.querySelector('input[name="id"]').value = variantId;

      // Submit and show mini cart
      const productForm = new FormData(form);
      productForm.append("sections", ["mini-cart"]);
      productForm.delete("option1");
      productForm.delete("option2");
      productForm.delete("option3");
      const response = fetch(`${window.themeVariables.routes.cartAddUrl}.js`, {
        body: productForm,
        method: "POST",
        headers: {
          "X-Requested-With": "XMLHttpRequest"
        }
      }).then(response => response.json())
        .then(data => {
          // Show notification
          if (data.status != 422) {
            form.dispatchEvent(new CustomEvent("cart-notification:show", {
              bubbles: true,
              cancelable: true,
              detail: {
                status: "success"
              }
            }));
          } else {
            form.dispatchEvent(new CustomEvent("cart-notification:show", {
              bubbles: true,
              cancelable: true,
              detail: {
                status: data.ok ? "success" : "error",
                error: data.description || ""
              }
            }));
          }
        }).catch(error => {
          console.log('error', error)
        });
    }
  }

  /**
   * Initializes thank you page with package product info.
   * Fetches product data and populates recommended products.
   * 
   * @returns {void}
   */
  initRequiredPackageThankYou() {
    let _this = this;
    let handle = sessionStorage.getItem('required_package_handle');
    fetch("/products/" + handle + ".json")
      .then(response => response.json())
      .then(data => {
        _this.parseRequiredPackageThankYou(data.product)
      }).catch(error => {
      console.log('error', error)
    });
  }

  /**
   * Parses product data and builds thank you page recommendation cards.
   * 
   * @param {Object} product - Product object from API
   * @returns {void}
   */
  parseRequiredPackageThankYou(product) {
    let _this = this;
    let recommended = sessionStorage.getItem('required_package_recommended');
    if (recommended != null) recommended = JSON.parse(recommended);
    
    // Set product image
    let img = document.querySelector('.required-packages-thank-you .product-image img');
    let recommendedContainer = document.querySelector('.required-packages-thank-you .required-packages-recommended');
    img.setAttribute('src', product.image.src);
    img.setAttribute('alt', product.image.alt);

    // Get template
    let tpl = document.querySelector('.required-packages-thank-you .product-card-tpl');

    // Build recommendation cards
    if (recommended != null) {
      recommended.forEach(function (prod) {
        let clone = tpl.cloneNode(true);
        clone.classList.remove('product-card-tpl');
        clone.classList.add('product-card');
        clone.dataset.handle = prod.handle;
        clone.dataset.variants = JSON.stringify(prod.variants);
        clone.querySelector('img').setAttribute('src', prod.featured_image);
        clone.querySelector('.title').innerHTML = `<a href="/products/${prod.handle}/">${prod.title}</a>`;
        clone.querySelector('.image').setAttribute('href', `/products/${prod.handle}/`);

        // Handle variants - show option dropdowns if multiple
        if (prod.variants != null && prod.variants.length > 1) {
          let optionsData = _this.parseRequiredPackageThankYouOptions(prod);

          // Append options to dropdowns
          for (const optionName in optionsData) {
            if (optionsData.hasOwnProperty(optionName)) {
              let values = optionsData[optionName];
              let targetCont = clone.querySelector('.field.' + optionName);

              if (targetCont) {
                let target = targetCont.querySelector('select');

                values.forEach(function (value) {
                  let opt = document.createElement('option');
                  opt.setAttribute('value', value);
                  opt.innerHTML = value;
                  target.appendChild(opt);
                });

                targetCont.classList.remove('hidden');
              }
            }
          }
        }

        recommendedContainer.appendChild(clone);
      });

      tpl.closest('.recommended-container').classList.remove('hidden');
    }

    _this.clearSession();
  }

  /**
   * Parses product options for thank you page variant selectors.
   * 
   * @param {Object} prod - Product object
   * @returns {Object} Options data keyed by option name
   */
  parseRequiredPackageThankYouOptions(prod) {
    let data = {};

    let i = 1;
    prod.options.forEach(function (option) {
      if (typeof data[option] == "undefined") {
        data[option] = [];
      }

      prod.variants.forEach(function (variant) {
        if (!data[option].includes(variant['option' + i])) {
          data[option].push(variant['option' + i]);
        }
      });

      i++;
    });

    return data;
  }

  /**
   * Initializes the required package flow on product pages.
   * Determines current step, sets up navigation, and configures cart.
   * 
   * @param {boolean} [isParent=false] - Whether this is the parent/kit product
   * @returns {void}
   */
  initRequiredPackage(isParent = false) {
    let _this = this;
    let cont = document.querySelector('.required-packages-container');
    if (cont == null) return;
    let productHandle = cont.dataset.productHandle;

    let products = JSON.parse(sessionStorage.getItem('required_package_products'));

    let currentIndex = null;
    for (let i = 0; i < products.length; i++) {
      if (products[i].handle == productHandle) {
        currentIndex = i;
      }
    }
    let addonProduct = sessionStorage.getItem('required_package_addon')

    // Check if this is an addon product
    let isAddon = false;
    if (addonProduct) {
      if (window.location.href.indexOf("products/" + addonProduct) > -1 || window.location.href.indexOf("products/" + encodeURI(addonProduct)) > -1) {
        document.querySelector('body').classList.add('is-kit-addon');
        currentIndex = products.length - 1;
        isAddon = true;
      }
    }

    fetch("/cart.js")
      .then(response => response.json())
      .then(data => {
        _this.parseRequiredPackage(data, products, currentIndex, isAddon, isParent);
      }).catch(error => {
      console.log('error', error)
    });
  }

  /**
   * Processes and updates the package flow UI.
   * Handles navigation buttons, step tracking, and cart attribute updates.
   * 
   * @param {Object} data - Cart data
   * @param {Array} products - All products in package
   * @param {number} currentIndex - Current product index
   * @param {boolean} isAddon - Whether viewing addon
   * @param {boolean} isParent - Whether viewing parent
   * @returns {void}
   */
  parseRequiredPackage(data, products, currentIndex, isAddon, isParent) {
    let cont = document.querySelector('.required-packages-container');
    let productHandle = cont.dataset.productHandle;
    let items = data.items;
    let prevItem = null;
    let nextItem = null;
    let currentItem = null;
    let currentCartItem = null;
    let total = products.length;
    let clearBundle = false;

    let i = 0;
    let current = i;
    let step = currentIndex + 1;
    let _uniqueId = sessionStorage.getItem('required_package_unique_id');

    // Validate kit ID
    if (!this.kitId) {
      this.clearKitItems(_uniqueId, data);
      document.querySelector('body').classList.remove('required-package-initialized');
      return;
    }

    // Determine current progress by checking cart items
    products.forEach(function (_product) {
      let inCart = false;
      items.forEach(function (item) {
        if (item.properties.hasOwnProperty('_required_package_unique_id')) {
          let uniqueId = item.properties['_required_package_unique_id'].toString();
          if (uniqueId === _uniqueId && item.handle === _product.handle) {
            inCart = true;
            current++;
            if (item.handle === productHandle) {
              currentCartItem = item;
            }
          }
        }
      });
      if (!nextItem && _product.handle !== productHandle) {
        nextItem = _product;
      } else if (_product.handle === productHandle) {
        currentItem = _product;
      }
      i++;
    });

    // Determine navigation
    if (currentIndex > 0) {
      prevItem = products[currentIndex - 1];
    }
    if (isAddon) {
      prevItem = products[products.length - 1];
    }
    if (typeof products[step] != "undefined" && step !== products.length) {
      nextItem = products[step];
    }

    if (currentIndex != null && !isAddon) {
      currentItem = products[currentIndex];
    }

    if (step === products.length) {
      nextItem = null
    }

    // Check completion status
    if (total === current) {
      let packagesInCart;
      let uniqueIds = [];
      items.forEach(item => {
        if (item.properties.hasOwnProperty('_required_package_unique_id')) {
          let uniqueId = item.properties['_required_package_unique_id'];
          if (!uniqueIds.includes(uniqueId)) {
            uniqueIds.push(uniqueId);
          }
        }
      });

      packagesInCart = uniqueIds.length;

      // Update cart attribute
      var formData = new FormData();
      formData.append("attributes[_required_packages_in_cart]", packagesInCart.toString());
      fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        body: formData
      })
        .then(response => response.json())
        .then(data => {
          // Clear session on completion
          sessionStorage.removeItem('required_package_initialized');
          eraseCookie('required_package_initialized');
          sessionStorage.removeItem('required_package_handle');
          sessionStorage.removeItem('required_package_unique_id');
        });
    } else if (total > current) {
      // Check if we need to redirect or continue
      if (window.location.href.indexOf("cart") > -1) {
        this.clearKitItems(_uniqueId, data);
      }

      // Check if current product should be in flow
      let isInKitProducts = false;
      products.some((item) => {
        if (item.handle === productHandle) isInKitProducts = true;
      });

      if (!isParent && !isInKitProducts && !isAddon) clearBundle = true;
    }
    
    // Redirect if bundle should be cleared
    if (clearBundle && !document.querySelector('body').classList.contains('template-cart') && !document.querySelector('body').classList.contains('template-suffix-packages-thank-you')) {
      cont.classList.add('hidden');
      document.querySelector('body').classList.remove('required-package-initialized');
      this.clearKitItems(_uniqueId, data);
      return;
    }

    // Update button hrefs
    let btn = cont.querySelector('.add-required-package-product-to-cart');

    if(btn) {
      btn.dataset.currentStep = step.toString();

      // Set next product URL
      if (nextItem) {
        btn.setAttribute('href', `/products/${nextItem.handle}?kit=${this.kitId}`);
        btn.dataset.lastItem = 'false';
      } else {
        // Last item - go to addon or finish
        if (sessionStorage.getItem('required_package_addon') != null && !isAddon) {
          btn.setAttribute('href', `/products/${sessionStorage.getItem('required_package_addon')}?kit=${this.kitId}`);
        } else {
          btn.setAttribute('href', document.querySelector('.required-package-addon-add').getAttribute('href') + '?kit=' + this.kitId);
        }
        btn.dataset.lastItem = 'true';
      }
    }

    // Set back button
    if (prevItem && currentIndex > 0 || prevItem && currentIndex == null) {
      cont.querySelector('.required-package-back').setAttribute('href', `/products/${prevItem.handle}?kit=${this.kitId}`);
      cont.querySelector('.required-package-back').dataset.productId = prevItem.id;
      cont.querySelector('.required-package-back').classList.remove('hidden');
    }

    // Set quantity requirements
    if (currentItem) {
      cont.dataset['requiredQuantity'] = currentItem.quantity;
      document.querySelector('input[name="quantity"]').value = parseInt(currentItem.quantity);
      document.querySelector('input[name="quantity"]').setAttribute('min', currentItem.quantity);
      document.querySelector('input[name="quantity"]').setAttribute('value', currentItem.quantity);
    }

    // Pre-fill custom fields if already personalized
    if (currentCartItem) {
      for (let prop in currentCartItem.properties) {
        if (document.querySelector('input[name="properties[' + prop + ']"]') != null) {
          document.querySelector('input[name="properties[' + prop + ']"]').value = currentCartItem.properties[prop];
        }
      }
    }

    // Update progress display
    if (current > total) current = total;

    let progressContainer = cont.querySelector('.progress-container');
    progressContainer.querySelector('.current').innerHTML = step.toString();
    progressContainer.querySelector('.total').innerHTML = total.toString();
    progressContainer.querySelector('.percentage .bg').style.width = (step / total) * 100 + '%';
  }

  /**
   * Removes all kit items from cart.
   * 
   * @param {string} uniqueId - The kit unique ID
   * @param {Object} [data=null] - Optional cart data
   * @returns {void}
   */
  clearKitItems(uniqueId, data = null) {
    let _this = this;

    // Fetch cart if not provided
    if (data == null) {
      fetch("/cart.js")
        .then(response => response.json())
        .then(data => {
          _this.clearKitItems(uniqueId, data);
        }).catch(error => {
        console.log('error', error)
      });
      return;
    }

    let cartItems = data.items;
    let packagesInCart = parseInt(data.attributes['_required_packages_in_cart'])

    let updates = {};

    // Find and remove kit items
    cartItems.forEach(function (cartItem) {
      if (cartItem.properties.hasOwnProperty('_required_package_unique_id') && uniqueId == cartItem.properties['_required_package_unique_id']) {
        updates[cartItem.variant_id] = 0;
      }
    });

    let newPackagesInCart = packagesInCart - 1;

    let _data = {
      updates: updates,
      attributes: {
        '_required_packages_in_cart': newPackagesInCart
      }
    }

    // Update cart
    fetch('/cart/update.js', {
      'method': 'POST',
      'headers': {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(_data)
    }).then(response => {
      _this.clearSession();

      // Reload or refresh cart
      if (document.querySelector('body').classList.contains('template-cart')) {
        window.location.reload();
      } else {
        setTimeout(function () {
          const cartContent = response;
          cartContent["sections"] = response["sections"];
          document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", {
            bubbles: true,
            detail: {
              cart: cartContent,
              openMiniCart: true
            }
          }));
        }, 1000)
      }
    });
  }

  /**
   * Clears all session storage and cookies for package tracking.
   * 
   * @returns {void}
   */
  clearSession() {
    sessionStorage.removeItem('required_package_parent_id');
    sessionStorage.removeItem('required_package_products');
    sessionStorage.removeItem('required_package_addon');
    sessionStorage.removeItem('required_package_recommended');
    sessionStorage.removeItem('required_package_initialized');
    eraseCookie('required_package_initialized');
    sessionStorage.removeItem('required_package_handle');
    sessionStorage.removeItem('required_package_unique_id');
  }
}

// Auto-instantiate on load
new RequiredPackages();
